import type { Clip, ClipKind, MediaMap, TextPreset, TextStyle, Timeline, TimelinePatch, Track, TrackKind } from './types';

/**
 * 本格編集のタイムライン操作（純関数）。DOM・ドラッグから切り離して jest で確かめられるようにする
 * （song-ad/timelineEditor.ts と同じ考え方）。ルールは renewal-ui-spec.md §11.3。
 */

export const FPS = 30;
export const MIN_CLIP_SECONDS = 0.2;
export const SPLIT_MARGIN = 0.1;
export const SNAP_PX = 8;
export const MAX_HISTORY = 100;
export const MERGE_WINDOW_MS = 900;
export const SPEEDS = [0.5, 0.75, 1, 1.5, 2] as const;

export const TEXT_PRESETS: Record<TextPreset, { label: string } & Omit<TextStyle, 'preset'>> = {
  zabuton: { label: 'テロップ', size: 17, color: '#FFFFFF', bg: 'rgba(29,25,39,.78)', y: 76 },
  caption: { label: '字幕', size: 20, color: '#FFFFFF', bg: 'none', y: 74 },
  title: { label: 'タイトル', size: 32, color: '#FFFFFF', bg: 'none', y: 22 },
  bubble: { label: '吹き出し', size: 15, color: '#1D1927', bg: '#FFFFFF', y: 16 },
  cta: { label: 'ボタン', size: 14, color: '#1D1927', bg: '#FFFFFF', y: 90 },
  label: { label: '表記', size: 12, color: '#FFFFFF', bg: 'rgba(29,25,39,.55)', y: 4 },
};

export const round3 = (n: number): number => Math.round(n * 1000) / 1000;
export const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v));
export const clipEnd = (c: Clip): number => round3(c.start + c.dur);

let idCounter = 0;
export function newId(prefix = 'c'): string {
  idCounter += 1;
  const rand = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID().replace(/-/g, '').slice(0, 8) : Math.random().toString(36).slice(2, 10);
  return `${prefix}${rand}${idCounter.toString(36)}`;
}

export function presetStyle(preset: TextPreset): TextStyle {
  const { label: _label, ...rest } = TEXT_PRESETS[preset];
  return { preset, ...rest };
}

export function mainTrackIndex(tl: Timeline): number {
  return tl.tracks.findIndex((t) => t.kind === 'video' && t.magnetic);
}

export function bgmTrackIndex(tl: Timeline): number {
  const a1 = tl.tracks.findIndex((t) => t.id === 'A1');
  return a1 >= 0 ? a1 : tl.tracks.findIndex((t) => t.kind === 'audio');
}

/** 全長（すべてのクリップの終わり。最低 1 秒） */
export function totalDuration(tl: Timeline): number {
  let end = 0;
  tl.tracks.forEach((t) => t.clips.forEach((c) => { end = Math.max(end, clipEnd(c)); }));
  return round3(end);
}

export function findClip(tl: Timeline, id: string): { trackIndex: number; clipIndex: number; track: Track; clip: Clip } | null {
  for (let t = 0; t < tl.tracks.length; t += 1) {
    const c = tl.tracks[t].clips.findIndex((clip) => clip.id === id);
    if (c >= 0) return { trackIndex: t, clipIndex: c, track: tl.tracks[t], clip: tl.tracks[t].clips[c] };
  }
  return null;
}

export function packMagnetic(clips: Clip[]): Clip[] {
  let cursor = 0;
  return clips.map((c) => {
    const next = { ...c, start: round3(cursor) };
    cursor += c.dur;
    return next;
  });
}

const sortByStart = (clips: Clip[]): Clip[] => [...clips].sort((a, b) => a.start - b.start);

function replaceTrack(tl: Timeline, index: number, track: Track): Timeline {
  const tracks = tl.tracks.slice();
  tracks[index] = track;
  return { ...tl, tracks };
}

/** 素材の長さから決まる、そのクリップが取れる最大の長さ（画像・テキストは上限なし） */
export function maxDuration(clip: Clip): number {
  if ((clip.kind === 'video' || clip.kind === 'audio') && clip.srcDur && clip.srcDur > 0) {
    return round3(Math.max(MIN_CLIP_SECONDS, (clip.srcDur - clip.in) / (clip.speed ?? 1)));
  }
  return Number.POSITIVE_INFINITY;
}

/** 同じトラックの他のクリップと重ならない位置へ後ろにずらす（通常トラック） */
export function avoidOverlap(clips: Clip[], clip: Clip): number {
  let start = Math.max(0, clip.start);
  const others = sortByStart(clips.filter((c) => c.id !== clip.id));
  let moved = true;
  while (moved) {
    moved = false;
    for (const o of others) {
      if (start < clipEnd(o) - 0.001 && start + clip.dur > o.start + 0.001) {
        start = clipEnd(o);
        moved = true;
      }
    }
  }
  return round3(start);
}

/** 吸着: 0・再生位置・他クリップの両端から SNAP_PX 以内なら寄せる */
export function snapCandidates(tl: Timeline, playhead: number, exclude: string[] = []): number[] {
  const points = [0, playhead];
  tl.tracks.forEach((t) => t.clips.forEach((c) => {
    if (!exclude.includes(c.id)) points.push(c.start, clipEnd(c));
  }));
  return points;
}

export function snap(time: number, candidates: number[], pps: number, enabled = true): number {
  if (!enabled) return round3(Math.max(0, time));
  const threshold = SNAP_PX / pps;
  let best = time;
  let bestDist = threshold;
  candidates.forEach((p) => {
    const d = Math.abs(p - time);
    if (d <= bestDist) {
      best = p;
      bestDist = d;
    }
  });
  return round3(Math.max(0, best));
}

/**
 * クリップの移動。同じ種類のトラックへ移せる。
 * 磁石トラックへは「中心位置で並び順を決めて詰め直す」、通常トラックは重なりを避けて後ろへずらす。
 */
export function moveClip(tl: Timeline, clipId: string, newStart: number, targetTrackId?: string): Timeline {
  const found = findClip(tl, clipId);
  if (!found || found.track.locked) return tl;
  const target = targetTrackId ? tl.tracks.findIndex((t) => t.id === targetTrackId) : found.trackIndex;
  if (target < 0) return tl;
  const targetTrack = tl.tracks[target];
  if (targetTrack.locked || !kindFits(targetTrack.kind, found.clip.kind)) return tl;

  let next = tl;
  const moved: Clip = { ...found.clip, start: round3(Math.max(0, newStart)) };

  // 元のトラックから外す
  const sourceClips = found.track.clips.filter((c) => c.id !== clipId);
  next = replaceTrack(next, found.trackIndex, {
    ...found.track,
    clips: found.track.magnetic ? packMagnetic(sourceClips) : sourceClips,
  });

  const dest = next.tracks[target];
  if (dest.magnetic) {
    // 並び順は「ドラッグ中に見えていた位置」（同じトラック内なら詰める前の位置）の中心で決める
    const center = moved.start + moved.dur / 2;
    const others = dest.clips;
    const reference = target === found.trackIndex ? found.track.clips.filter((c) => c.id !== clipId) : others;
    const index = reference.filter((c) => c.start + c.dur / 2 < center).length;
    const clips = [...others.slice(0, index), moved, ...others.slice(index)];
    return replaceTrack(next, target, { ...dest, clips: packMagnetic(clips) });
  }
  const placed = { ...moved, start: avoidOverlap(dest.clips, moved) };
  return replaceTrack(next, target, { ...dest, clips: sortByStart([...dest.clips, placed]) });
}

export function kindFits(trackKind: TrackKind, clipKind: ClipKind): boolean {
  if (trackKind === 'video') return clipKind === 'video' || clipKind === 'image';
  if (trackKind === 'text') return clipKind === 'text';
  return clipKind === 'audio';
}

/**
 * 端のつまみ。右端: 0.2 ≤ dur ≤ (srcDur-in)/speed。左端: in をずらす（磁石トラックは start 固定で後ろが詰まる）。
 */
export function trimClip(tl: Timeline, clipId: string, edge: 'start' | 'end', time: number): Timeline {
  const found = findClip(tl, clipId);
  if (!found || found.track.locked) return tl;
  const { clip, track, trackIndex } = found;
  const speed = clip.speed ?? 1;
  let updated: Clip;

  if (edge === 'end') {
    const dur = clamp(time - clip.start, MIN_CLIP_SECONDS, maxDuration(clip));
    updated = { ...clip, dur: round3(dur) };
  } else {
    const end = clipEnd(clip);
    const usesSource = clip.kind === 'video' || clip.kind === 'audio';
    // 左へ伸ばせるのは素材の頭（in=0）まで
    const minStart = usesSource ? clip.start - clip.in / speed : 0;
    const start = clamp(time, Math.max(0, minStart), end - MIN_CLIP_SECONDS);
    const delta = start - clip.start;
    updated = {
      ...clip,
      start: round3(start),
      dur: round3(end - start),
      in: usesSource ? round3(Math.max(0, clip.in + delta * speed)) : clip.in,
    };
  }

  const clips = track.clips.map((c) => (c.id === clipId ? updated : c));
  return replaceTrack(tl, trackIndex, { ...track, clips: track.magnetic ? packMagnetic(clips) : clips });
}

/**
 * 分割（S / ⌘B）。選択クリップ（なければメイン映像で再生位置にかかるもの）を再生位置で 2 つに。
 * 端から 0.1 秒以内は分割しない（null）。
 */
export function splitAt(tl: Timeline, time: number, clipId?: string | null): { timeline: Timeline; newId: string } | null {
  let found = clipId ? findClip(tl, clipId) : null;
  if (found && !(time > found.clip.start + SPLIT_MARGIN && time < clipEnd(found.clip) - SPLIT_MARGIN)) found = null;
  if (!found) {
    const main = mainTrackIndex(tl);
    if (main < 0) return null;
    const clip = tl.tracks[main].clips.find((c) => time > c.start + SPLIT_MARGIN && time < clipEnd(c) - SPLIT_MARGIN);
    if (!clip) return null;
    found = findClip(tl, clip.id);
  }
  if (!found || found.track.locked) return null;

  const { clip, track, trackIndex, clipIndex } = found;
  const offset = time - clip.start;
  const usesSource = clip.kind === 'video' || clip.kind === 'audio';
  const first: Clip = { ...clip, dur: round3(offset), fadeOut: clip.kind === 'audio' ? 0 : clip.fadeOut };
  const id = newId();
  const second: Clip = {
    ...clip,
    id,
    start: round3(time),
    dur: round3(clip.dur - offset),
    in: usesSource ? round3(clip.in + offset * (clip.speed ?? 1)) : clip.in,
    transition: clip.kind === 'video' || clip.kind === 'image' ? 'none' : clip.transition,
    fadeIn: clip.kind === 'audio' ? 0 : clip.fadeIn,
  };
  if (first.fadeOut === undefined) delete first.fadeOut;
  if (second.fadeIn === undefined) delete second.fadeIn;
  if (second.transition === undefined) delete second.transition;

  const clips = [...track.clips.slice(0, clipIndex), first, second, ...track.clips.slice(clipIndex + 1)];
  return { timeline: replaceTrack(tl, trackIndex, { ...track, clips }), newId: id };
}

/** 削除（ロック中のトラックは除外）。磁石トラックは詰まる */
export function deleteClips(tl: Timeline, ids: string[]): Timeline {
  if (ids.length === 0) return tl;
  return {
    ...tl,
    tracks: tl.tracks.map((t) => {
      if (t.locked || !t.clips.some((c) => ids.includes(c.id))) return t;
      const clips = t.clips.filter((c) => !ids.includes(c.id));
      return { ...t, clips: t.magnetic ? packMagnetic(clips) : clips };
    }),
  };
}

/** 複製（⌘D）。直後に挿入（磁石トラックは挿入して詰め直し、通常は空きへ） */
export function duplicateClips(tl: Timeline, ids: string[]): { timeline: Timeline; ids: string[] } {
  let next = tl;
  const created: string[] = [];
  ids.forEach((id) => {
    const found = findClip(next, id);
    if (!found || found.track.locked) return;
    const copy: Clip = { ...found.clip, id: newId(), start: clipEnd(found.clip) };
    created.push(copy.id);
    const { track, trackIndex, clipIndex } = found;
    if (track.magnetic) {
      const clips = [...track.clips.slice(0, clipIndex + 1), copy, ...track.clips.slice(clipIndex + 1)];
      next = replaceTrack(next, trackIndex, { ...track, clips: packMagnetic(clips) });
    } else {
      const placed = { ...copy, start: avoidOverlap(track.clips, copy) };
      next = replaceTrack(next, trackIndex, { ...track, clips: sortByStart([...track.clips, placed]) });
    }
  });
  return { timeline: next, ids: created };
}

const TRACK_LABEL: Record<TrackKind, string> = { video: '映像', text: 'テキスト', audio: '音' };

/**
 * 素材の追加（押す＝再生位置、ドラッグ＝落とした位置とトラック）。
 * 追加先: 指定トラック → 映像なら磁石トラック → 空いている同種トラック → なければ新トラック。
 * 磁石トラックに差し込むと、BGM（A1）とロック以外のトラックの、挿入点以降のクリップも同じ秒だけ後ろへずらす。
 */
export function insertClip(tl: Timeline, clip: Clip, at: number, targetTrackId?: string | null): { timeline: Timeline; trackId: string } {
  const kind: TrackKind = clip.kind === 'image' || clip.kind === 'video' ? 'video' : clip.kind === 'text' ? 'text' : 'audio';
  let index = targetTrackId ? tl.tracks.findIndex((t) => t.id === targetTrackId && !t.locked && kindFits(t.kind, clip.kind)) : -1;
  if (index < 0 && kind === 'video') {
    const main = mainTrackIndex(tl);
    if (main >= 0 && !tl.tracks[main].locked) index = main;
  }
  if (index < 0) {
    index = tl.tracks.findIndex((t) => t.kind === kind && !t.locked && !t.magnetic && t.id !== 'A1'
      && t.clips.every((c) => at >= clipEnd(c) - 0.001 || at + clip.dur <= c.start + 0.001));
  }

  let next = tl;
  if (index < 0) {
    const count = tl.tracks.filter((t) => t.kind === kind).length + 1;
    const track: Track = { id: newId('t'), kind, name: `${TRACK_LABEL[kind]}${count}`, clips: [] };
    const lastOfKind = tl.tracks.map((t) => t.kind).lastIndexOf(kind);
    const position = lastOfKind >= 0 ? lastOfKind + 1 : kind === 'audio' ? tl.tracks.length : 0;
    const tracks = tl.tracks.slice();
    tracks.splice(position, 0, track);
    next = { ...tl, tracks };
    index = position;
  }

  const track = next.tracks[index];
  if (!track.magnetic) {
    const placed = { ...clip, start: round3(Math.max(0, at)) };
    placed.start = avoidOverlap(track.clips, placed);
    return { timeline: replaceTrack(next, index, { ...track, clips: sortByStart([...track.clips, placed]) }), trackId: track.id };
  }

  // 磁石トラック: at を含むクリップの前後（中心で判断）に差し込む
  const position = track.clips.filter((c) => c.start + c.dur / 2 <= at).length;
  const insertAt = position === 0 ? 0 : clipEnd(track.clips[position - 1]);
  const clips = packMagnetic([...track.clips.slice(0, position), { ...clip, start: insertAt }, ...track.clips.slice(position)]);
  next = replaceTrack(next, index, { ...track, clips });

  const bgm = bgmTrackIndex(next);
  next = {
    ...next,
    tracks: next.tracks.map((t, i) => {
      if (i === index || i === bgm || t.locked) return t;
      return { ...t, clips: t.clips.map((c) => (c.start >= insertAt - 0.001 ? { ...c, start: round3(c.start + clip.dur) } : c)) };
    }),
  };
  return { timeline: next, trackId: track.id };
}

/** プロパティの変更。磁石トラックで長さが変われば詰め直す */
export function updateClip(tl: Timeline, clipId: string, patch: Partial<Clip>): Timeline {
  const found = findClip(tl, clipId);
  if (!found || found.track.locked) return tl;
  let updated: Clip = { ...found.clip, ...patch };
  if (patch.speed !== undefined && patch.speed !== (found.clip.speed ?? 1)) {
    const oldSpeed = found.clip.speed ?? 1;
    const dur = (found.clip.dur * oldSpeed) / patch.speed;
    updated = { ...updated, dur: round3(clamp(dur, MIN_CLIP_SECONDS, maxDuration(updated))) };
  }
  if (patch.dur !== undefined) {
    updated.dur = round3(clamp(patch.dur, MIN_CLIP_SECONDS, maxDuration(updated)));
  }
  const clips = found.track.clips.map((c) => (c.id === clipId ? updated : c));
  const track = { ...found.track, clips: found.track.magnetic ? packMagnetic(clips) : sortByStart(clips) };
  return replaceTrack(tl, found.trackIndex, track);
}

export function updateTrack(tl: Timeline, trackId: string, patch: Partial<Track>): Timeline {
  return { ...tl, tracks: tl.tracks.map((t) => (t.id === trackId ? { ...t, ...patch } : t)) };
}

/** サーバーの「AIに頼む」の差分を当てる（同じ id のトラックは置き換え、無ければ index に差し込む） */
export function applyPatch(tl: Timeline, patch: TimelinePatch): Timeline {
  let tracks = tl.tracks.filter((t) => !patch.remove.includes(t.id));
  patch.upsert.forEach(({ index, track }) => {
    const existing = tracks.findIndex((t) => t.id === track.id);
    if (existing >= 0) {
      tracks = tracks.map((t, i) => (i === existing ? track : t));
    } else {
      tracks = [...tracks.slice(0, index), track, ...tracks.slice(index)];
    }
  });
  return { ...tl, tracks, ducking: patch.ducking ?? tl.ducking };
}

/** 保存用（表示用の src / poster / missing を落とす） */
export function stripForSave(tl: Timeline): Timeline {
  return {
    ...tl,
    tracks: tl.tracks.map((t) => ({
      ...t,
      clips: t.clips.map(({ src: _src, poster: _poster, missing: _missing, ...rest }) => rest),
    })),
  };
}

/** タイムラインに入っている src を ref ごとに集める（読み込み直後） */
export function collectMedia(tl: Timeline): MediaMap {
  const map: MediaMap = {};
  tl.tracks.forEach((t) => t.clips.forEach((c) => {
    if (c.ref && c.src) map[c.ref] = { src: c.src, poster: c.poster ?? null };
  }));
  return map;
}

/** 声が鳴っている区間（BGM を下げる）。映像で音量>0、音で効果音以外 */
export function voiceWindows(tl: Timeline): [number, number][] {
  const bgm = bgmTrackIndex(tl);
  const windows: [number, number][] = [];
  tl.tracks.forEach((t, i) => {
    if (i === bgm || t.muted) return;
    t.clips.forEach((c) => {
      const voiced = (c.kind === 'video' && (c.volume ?? 1) > 0) || (c.kind === 'audio' && !(c.ref ?? '').startsWith('sfx:'));
      if (voiced) windows.push([c.start, clipEnd(c)]);
    });
  });
  return windows;
}

/* ------------------------------------------------------------------ */
/* 元に戻す・やり直す（100段。同じプロパティを 0.9 秒以内に続けて変えたら 1 段にまとめる） */
/* ------------------------------------------------------------------ */

export interface History {
  past: Timeline[];
  present: Timeline;
  future: Timeline[];
  lastKey: string | null;
  lastAt: number;
}

export function initHistory(tl: Timeline): History {
  return { past: [], present: tl, future: [], lastKey: null, lastAt: 0 };
}

export function commit(h: History, next: Timeline, key: string | null = null, now = Date.now()): History {
  if (next === h.present) return h;
  if (key !== null && key === h.lastKey && now - h.lastAt < MERGE_WINDOW_MS) {
    return { ...h, present: next, future: [], lastAt: now };
  }
  return { past: [...h.past, h.present].slice(-MAX_HISTORY), present: next, future: [], lastKey: key, lastAt: now };
}

export function undo(h: History): History {
  if (h.past.length === 0) return h;
  return { past: h.past.slice(0, -1), present: h.past[h.past.length - 1], future: [h.present, ...h.future], lastKey: null, lastAt: 0 };
}

export function redo(h: History): History {
  if (h.future.length === 0) return h;
  const [next, ...rest] = h.future;
  return { past: [...h.past, h.present].slice(-MAX_HISTORY), present: next, future: rest, lastKey: null, lastAt: 0 };
}

/** mm:ss.ff（30fps のコマ） */
export function formatTimecode(seconds: number): string {
  const safe = Math.max(0, seconds);
  const m = Math.floor(safe / 60);
  const s = Math.floor(safe % 60);
  const f = Math.floor((safe - Math.floor(safe)) * FPS);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(f).padStart(2, '0')}`;
}

/** ルーラーの目盛（pps に応じて） */
export function rulerSteps(pps: number): { minor: number; major: number } {
  if (pps >= 120) return { minor: 0.25, major: 1 };
  if (pps >= 60) return { minor: 0.5, major: 2 };
  if (pps >= 30) return { minor: 1, major: 5 };
  return { minor: 2, major: 10 };
}

export function compositionSize(ratio: Timeline['ratio']): { width: number; height: number } {
  if (ratio === '16:9') return { width: 1920, height: 1080 };
  if (ratio === '1:1') return { width: 1080, height: 1080 };
  return { width: 1080, height: 1920 };
}

/**
 * プレビューを押した位置のクリップ（テキストは縦位置が近いもの → 重ね映像（上のトラック優先） → メイン映像）
 */
export function clipAtPoint(timeline: Timeline, time: number, yPercent: number): string | null {
  const active = (c: Clip) => time >= c.start && time < c.start + c.dur;
  for (const track of timeline.tracks) {
    if (track.kind !== 'text' || track.hidden) continue;
    const hit = track.clips.find((c) => active(c) && c.style && Math.abs(c.style.y - yPercent) < 6);
    if (hit) return hit.id;
  }
  for (const track of timeline.tracks) {
    if (track.kind !== 'video' || track.hidden) continue;
    const hit = track.clips.find(active);
    if (hit) return hit.id;
  }
  return null;
}
